import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';
import { useSiteNames } from './useSiteCatalog';

const STATUS_COLORS = {
    Actual: { bg: '#28a745', fg: '#ffffff' },
    Projected: { bg: '#6c757d', fg: '#ffffff' },
    Reconciled: { bg: '#17a2b8', fg: '#ffffff' },
    Holiday: { bg: '#ffc107', fg: '#212529' },
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const ALL_QUARTERS = 'ALL';

// Option-year quarters (Apr–Mar), matching the PMF RFPA/SCR tracker:
// Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
const getOptionYearQuarter = (date) => {
    const month = date.getMonth(); // 0-based
    const year = date.getFullYear();
    if (month >= 3 && month <= 5) return { oy: year, q: 1, startMonth: 3 };
    if (month >= 6 && month <= 8) return { oy: year, q: 2, startMonth: 6 };
    if (month >= 9 && month <= 11) return { oy: year, q: 3, startMonth: 9 };
    return { oy: year - 1, q: 4, startMonth: 0 }; // Jan–Mar belongs to prior OY
};

const quarterKey = ({ oy, q }) => `OY${oy}-Q${q}`;

const quarterLabel = ({ oy, q }) => {
    const ranges = {
        1: 'Apr–Jun',
        2: 'Jul–Sep',
        3: 'Oct–Dec',
        4: 'Jan–Mar',
    };
    return `OY ${oy} Q${q} (${ranges[q]})`;
};

const getCurrentQuarterKey = () => quarterKey(getOptionYearQuarter(new Date()));

const getQuarterDateRange = (key) => {
    if (!key || key === ALL_QUARTERS) return null;
    const m = /^OY(\d{4})-Q([1-4])$/.exec(key);
    if (!m) return null;
    const oy = parseInt(m[1], 10);
    const q = parseInt(m[2], 10);
    const startMonthByQ = { 1: 3, 2: 6, 3: 9, 4: 0 };
    const startYear = q === 4 ? oy + 1 : oy;
    const startMonth = startMonthByQ[q];
    const start = new Date(startYear, startMonth, 1);
    const end = new Date(startYear, startMonth + 3, 0); // last day of quarter
    return { start, end };
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#e9eef3',
        rowAlt: isDark ? '#262626' : '#fafbfc',
        totalBg: isDark ? '#33383f' : '#eef3f8',
        grandTotalBg: isDark ? '#3a2f22' : '#fff4e0',
        monthBg: isDark ? '#20303f' : '#e3f0fb',
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px', color: colors.text },
        headerCard: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        navLink: { color: colors.link, textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '10px' },
        title: { fontSize: '24px', fontWeight: 'bold', color: colors.text, margin: '0 0 6px 0' },
        subtitle: { fontSize: '14px', color: colors.subtleText, margin: 0 },
        section: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: '20px',
        },
        sectionHeader: {
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.headerBg,
            flexWrap: 'wrap',
            gap: '10px',
        },
        sectionTitle: { fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: 0 },
        controls: {
            display: 'flex',
            gap: '12px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
        },
        exportButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        scrollableTableWrap: { overflowX: 'auto', overflowY: 'auto', maxHeight: '640px' },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: {
            position: 'sticky',
            top: 0,
            padding: '10px 10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: 'bold',
            color: colors.text,
            backgroundColor: colors.headerBg,
            borderBottom: `2px solid ${colors.border}`,
            whiteSpace: 'nowrap',
            zIndex: 2,
        },
        thLeft: { textAlign: 'left' },
        td: {
            padding: '7px 10px',
            fontSize: '12px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
        },
        tdLeft: { textAlign: 'left' },
        rowAlt: { backgroundColor: colors.rowAlt },
        monthRow: { backgroundColor: colors.monthBg },
        monthCell: {
            padding: '8px 10px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: colors.text,
            textAlign: 'left',
            borderBottom: `1px solid ${colors.border}`,
        },
        totalRow: { backgroundColor: colors.totalBg, fontWeight: 'bold' },
        grandTotalRow: { backgroundColor: colors.grandTotalBg, fontWeight: 'bold' },
        loadingBox: { padding: '40px', textAlign: 'center' },
        empty: { padding: '40px', textAlign: 'center', color: colors.subtleText },
        statusBadge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
        statChip: {
            backgroundColor: colors.headerBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '8px 14px',
            minWidth: '120px',
        },
        statsRow: { display: 'flex', gap: '16px', padding: '12px 20px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' },
        statLabel: { fontSize: '11px', color: colors.subtleText, marginBottom: '2px' },
        statValue: { fontSize: '18px', fontWeight: 'bold' },
        rowActionBtn: {
            padding: '3px 10px',
            fontSize: '11px',
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            background: 'transparent',
            color: colors.link,
            cursor: 'pointer',
            marginRight: '6px',
        },
        rowActionLink: {
            padding: '3px 8px',
            fontSize: '11px',
            border: 'none',
            background: 'transparent',
            color: colors.subtleText,
            cursor: 'pointer',
            textDecoration: 'underline',
        },
        modalOverlay: {
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            padding: '24px',
            width: '440px',
            maxWidth: '92vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: `0 6px 24px ${colors.shadow}`,
        },
        modalTitle: { fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 4px 0' },
        modalSub: { fontSize: '12px', color: colors.subtleText, margin: '0 0 16px 0' },
        fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
        fieldLabel: { display: 'block', fontSize: '11px', color: colors.subtleText, marginBottom: '4px' },
        fieldInput: {
            width: '100%',
            padding: '8px 10px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            boxSizing: 'border-box',
        },
        previewBox: {
            backgroundColor: colors.headerBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '10px 12px',
            display: 'flex',
            gap: '18px',
            marginBottom: '18px',
        },
        modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px' },
        primaryBtn: {
            padding: '8px 18px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        secondaryBtn: {
            padding: '8px 18px',
            backgroundColor: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
    };
};

const EDIT_FIELDS = [
    { key: 'rfp', label: 'RFP (RFPA available)' },
    { key: 'tar', label: 'TAR (Required)' },
    { key: 'sfl', label: 'Sfl (Sorties Flown)' },
    { key: 'sked', label: 'Sked (Scheduled)' },
    { key: 'swx', label: 'Swx (Weather Cxl)' },
    { key: 'sxo', label: 'Sxo (Ops Cxl)' },
];

const extractList = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (responseData && Array.isArray(responseData.results)) return responseData.results;
    return [];
};

const parseDateOnly = (s) => {
    if (!s) return null;
    const str = String(s).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const fmtDate = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : '');
const fmtInt = (v) => (v === null || v === undefined || v === '' ? '' : v);
const fmtPct = (v) => (v === null || v === undefined || v === '' ? '—' : `${(Number(v) * 100).toFixed(1)}%`);

const isBlank = (v) => v === '' || v === null || v === undefined;
const toNum = (v) => (isBlank(v) ? 0 : Number(v));

// Mirrors backend recompute_rates_from_counts so edits preview correctly.
const computePreview = (f) => {
    let rfpa = null;
    if (!(isBlank(f.rfp) && isBlank(f.tar))) {
        const tar = toNum(f.tar);
        rfpa = tar === 0 || toNum(f.rfp) > tar ? 1 : toNum(f.rfp) / tar;
    }
    let scr = null;
    if (!(isBlank(f.sked) && isBlank(f.sfl))) {
        const denom = toNum(f.sked) - (toNum(f.swx) + toNum(f.sxo));
        scr = denom === 0 ? 1 : Math.max(0, Math.min(1, toNum(f.sfl) / denom));
    }
    const rates = [rfpa, scr].filter((v) => v !== null);
    const avg = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
    return { rfpa, scr, avg };
};

const COLUMNS = [
    { key: 'rfp', label: 'RFP' },
    { key: 'tar', label: 'TAR' },
    { key: 'sfl', label: 'Sfl' },
    { key: 'sked', label: 'Sked' },
    { key: 'swx', label: 'Swx' },
    { key: 'sxo', label: 'Sxo' },
];
const RATE_COLUMNS = [
    { key: 'rfpa', label: 'RFPA' },
    { key: 'scr', label: 'SCR' },
    { key: 'avg', label: 'AVG' },
];

function RfpaScrPage({ theme, embedded = false }) {
    const styles = getStyles(theme);
    const siteNames = useSiteNames();
    const [siteFilter, setSiteFilter] = useState('');
    const [quarterFilter, setQuarterFilter] = useState(getCurrentQuarterKey);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [editRow, setEditRow] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        if (!siteFilter) {
            setRows([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await api.get(`/rfpa-scr-daily/?site_name=${encodeURIComponent(siteFilter)}`);
            const list = extractList(res.data)
                .map((r) => ({ ...r, _date: parseDateOnly(r.date) }))
                .filter((r) => r._date)
                .sort((a, b) => a._date - b._date);
            setRows(list);
        } catch (err) {
            console.error('RFPA SCR fetch failed:', err);
            const status = err?.response?.status;
            setAlert({
                show: true,
                message: status === 404
                    ? 'RFPA SCR endpoint not found (backend may need a redeploy).'
                    : 'Failed to load RFPA SCR data.',
                type: 'error',
            });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [siteFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!siteFilter && siteNames.length) {
            setSiteFilter(siteNames[0]);
        }
    }, [siteNames, siteFilter]);

    const quarterOptions = useMemo(() => {
        const seen = new Map();
        for (const r of rows) {
            const info = getOptionYearQuarter(r._date);
            const key = quarterKey(info);
            if (!seen.has(key)) {
                seen.set(key, { key, label: quarterLabel(info), oy: info.oy, q: info.q });
            }
        }
        return Array.from(seen.values()).sort((a, b) => {
            if (a.oy !== b.oy) return b.oy - a.oy;
            return b.q - a.q;
        });
    }, [rows]);

    // Keep the selected quarter valid once data loads; prefer current quarter when available.
    useEffect(() => {
        if (loading || rows.length === 0) return;
        if (quarterFilter === ALL_QUARTERS) return;
        const keys = new Set(quarterOptions.map((q) => q.key));
        if (keys.has(quarterFilter)) return;
        const current = getCurrentQuarterKey();
        setQuarterFilter(keys.has(current) ? current : (quarterOptions[0]?.key || ALL_QUARTERS));
    }, [loading, rows.length, quarterOptions, quarterFilter]);

    const filteredRows = useMemo(() => {
        if (quarterFilter === ALL_QUARTERS) return rows;
        const range = getQuarterDateRange(quarterFilter);
        if (!range) return rows;
        return rows.filter((r) => r._date >= range.start && r._date <= range.end);
    }, [rows, quarterFilter]);

    const openEdit = (row) => {
        setForm({
            rfp: row.rfp ?? '', tar: row.tar ?? '', sfl: row.sfl ?? '',
            sked: row.sked ?? '', swx: row.swx ?? '', sxo: row.sxo ?? '',
        });
        setEditRow(row);
    };
    const closeEdit = () => { setEditRow(null); setForm({}); };
    const onFormChange = (key, val) => setForm((f) => ({ ...f, [key]: val }));
    const cleanInt = (v) => (isBlank(v) ? null : parseInt(v, 10));

    const saveReconcile = async () => {
        if (!editRow) return;
        setSaving(true);
        try {
            await api.patch(`/rfpa-scr-daily/${editRow.id}/`, {
                rfp: cleanInt(form.rfp), tar: cleanInt(form.tar), sfl: cleanInt(form.sfl),
                sked: cleanInt(form.sked), swx: cleanInt(form.swx), sxo: cleanInt(form.sxo),
                is_reconciled: true, status: 'Reconciled',
            });
            setAlert({ show: true, message: `Reconciled ${fmtDate(editRow._date)} for ${siteFilter}.`, type: 'success' });
            closeEdit();
            fetchData();
        } catch (err) {
            console.error('Reconcile save failed:', err);
            setAlert({ show: true, message: 'Failed to save reconciled values.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const unreconcile = async (row) => {
        setSaving(true);
        try {
            await api.patch(`/rfpa-scr-daily/${row.id}/`, { is_reconciled: false });
            setAlert({ show: true, message: `Reverted ${fmtDate(row._date)} to auto-synced values.`, type: 'success' });
            fetchData();
        } catch (err) {
            console.error('Unreconcile failed:', err);
            setAlert({ show: true, message: 'Failed to revert reconciliation.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Group rows by month, compute monthly subtotals and a grand total.
    const { groups, grandTotal } = useMemo(() => {
        const byMonth = new Map();
        for (const r of filteredRows) {
            const key = `${r._date.getFullYear()}-${r._date.getMonth()}`;
            if (!byMonth.has(key)) {
                byMonth.set(key, {
                    key,
                    label: `${MONTH_NAMES[r._date.getMonth()]} ${r._date.getFullYear()}`,
                    rows: [],
                });
            }
            byMonth.get(key).rows.push(r);
        }

        const sumTotals = (list) => {
            const totals = {};
            for (const c of COLUMNS) {
                const vals = list.map((r) => r[c.key]).filter((v) => v !== null && v !== undefined && v !== '');
                totals[c.key] = vals.length ? vals.reduce((a, b) => a + Number(b), 0) : null;
            }
            // Same formulas as daily rows, applied to the summed counts:
            // RFPA% = RFP/TAR, SCR% = Sfl/(Sked-(Swx+Sxo)), AVG = (RFPA+SCR)/2
            const rates = computePreview({
                rfp: totals.rfp, tar: totals.tar, sfl: totals.sfl,
                sked: totals.sked, swx: totals.swx, sxo: totals.sxo,
            });
            totals.rfpa = rates.rfpa;
            totals.scr = rates.scr;
            totals.avg = rates.avg;
            return totals;
        };

        const grouped = Array.from(byMonth.values()).map((g) => ({ ...g, totals: sumTotals(g.rows) }));
        return { groups: grouped, grandTotal: sumTotals(filteredRows) };
    }, [filteredRows]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(`RFPA SCR ${siteFilter}`);
            sheet.addRow([`RFPA SCR Daily Tracker - ${siteFilter}${quarterFilter === ALL_QUARTERS ? '' : ` - ${quarterFilter.replace('-', ' ')}`}`]);
            sheet.mergeCells(1, 1, 1, 11);
            sheet.getCell('A1').font = { size: 14, bold: true };
            sheet.addRow([]);
            const headers = ['Date', ...COLUMNS.map((c) => c.label), ...RATE_COLUMNS.map((c) => c.label), 'Status'];
            sheet.addRow(headers).font = { bold: true };

            groups.forEach((g) => {
                g.rows.forEach((r) => {
                    sheet.addRow([
                        fmtDate(r._date),
                        ...COLUMNS.map((c) => (r[c.key] ?? '')),
                        ...RATE_COLUMNS.map((c) => (r[c.key] === null || r[c.key] === undefined ? '' : Number(r[c.key]))),
                        r.status || '',
                    ]);
                });
                const t = g.totals;
                sheet.addRow([
                    `${g.label} Total`,
                    ...COLUMNS.map((c) => (t[c.key] ?? '')),
                    ...RATE_COLUMNS.map((c) => (t[c.key] === null ? '' : Number(t[c.key]))),
                    '',
                ]).font = { bold: true };
            });
            sheet.addRow([
                'Quarter / Total',
                ...COLUMNS.map((c) => (grandTotal[c.key] ?? '')),
                ...RATE_COLUMNS.map((c) => (grandTotal[c.key] === null ? '' : Number(grandTotal[c.key]))),
                '',
            ]).font = { bold: true };

            RATE_COLUMNS.forEach((c, i) => {
                sheet.getColumn(1 + COLUMNS.length + 1 + i).numFmt = '0.0%';
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const qSuffix = quarterFilter === ALL_QUARTERS ? 'AllQuarters' : quarterFilter;
            link.download = `RFPA_SCR_${siteFilter}_${qSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setAlert({ show: true, message: 'Excel file exported successfully.', type: 'success' });
        } catch (err) {
            console.error('RFPA SCR export error:', err);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const renderStatus = (status) => {
        if (!status) return null;
        const sc = STATUS_COLORS[status] || { bg: '#6c757d', fg: '#ffffff' };
        return <span style={{ ...styles.statusBadge, backgroundColor: sc.bg, color: sc.fg }}>{status}</span>;
    };

    const totalColSpan = 1 + COLUMNS.length + RATE_COLUMNS.length + 2;
    const preview = computePreview(form);
    const latestActual = useMemo(() => {
        const actuals = filteredRows.filter((r) => r.status === 'Actual');
        return actuals.length ? actuals[actuals.length - 1] : null;
    }, [filteredRows]);

    const selectedQuarterLabel = useMemo(() => {
        if (quarterFilter === ALL_QUARTERS) return 'All Quarters';
        const found = quarterOptions.find((q) => q.key === quarterFilter);
        if (found) return found.label;
        const m = /^OY(\d{4})-Q([1-4])$/.exec(quarterFilter);
        if (!m) return quarterFilter;
        return quarterLabel({ oy: parseInt(m[1], 10), q: parseInt(m[2], 10) });
    }, [quarterFilter, quarterOptions]);

    return (
        <div style={embedded ? undefined : styles.page}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />

            {!embedded && (
                <div style={styles.headerCard}>
                    <Link to="/" style={styles.navLink}>&larr; Back to Home</Link>
                    <h1 style={styles.title}>RFPA SCR</h1>
                    <p style={styles.subtitle}>
                        Daily RFPA (Ready For Production Availability) and SCR (Sortie Completion Rate)
                        quarterly tracker by site.
                    </p>
                </div>
            )}

            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>RFPA SCR — {siteFilter} — {selectedQuarterLabel}</h2>
                    <button
                        onClick={handleExport}
                        disabled={exporting || loading || filteredRows.length === 0}
                        style={{
                            ...styles.exportButton,
                            opacity: (exporting || loading || filteredRows.length === 0) ? 0.6 : 1,
                            cursor: (exporting || loading || filteredRows.length === 0) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>

                <div style={styles.statsRow}>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Days Tracked</div>
                        <div style={styles.statValue}>{filteredRows.length}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Qtr RFPA</div>
                        <div style={styles.statValue}>{fmtPct(grandTotal.rfpa)}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Qtr SCR</div>
                        <div style={styles.statValue}>{fmtPct(grandTotal.scr)}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Latest Actual</div>
                        <div style={styles.statValue}>{latestActual ? fmtDate(latestActual._date) : '—'}</div>
                    </div>
                </div>

                <div style={styles.controls}>
                    <label style={{ fontSize: '13px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>Site:</label>
                    <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} style={styles.select}>
                            {siteNames.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <label style={{ fontSize: '13px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>Quarter:</label>
                    <select value={quarterFilter} onChange={(e) => setQuarterFilter(e.target.value)} style={styles.select}>
                        <option value={ALL_QUARTERS}>All Quarters</option>
                        {quarterOptions.map((q) => (
                            <option key={q.key} value={q.key}>{q.label}</option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <div style={styles.loadingBox}><Spinner /></div>
                ) : filteredRows.length === 0 ? (
                    <div style={styles.empty}>
                        No RFPA SCR data found for {siteFilter}
                        {quarterFilter === ALL_QUARTERS ? '' : ` in ${selectedQuarterLabel}`}.
                    </div>
                ) : (
                    <div style={styles.scrollableTableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, ...styles.thLeft }}>Date</th>
                                    {COLUMNS.map((c) => (<th key={c.key} style={styles.th}>{c.label}</th>))}
                                    {RATE_COLUMNS.map((c) => (<th key={c.key} style={styles.th}>{c.label}</th>))}
                                    <th style={{ ...styles.th, ...styles.thLeft }}>Status</th>
                                    <th style={{ ...styles.th, ...styles.thLeft }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => (
                                    <React.Fragment key={g.key}>
                                        <tr style={styles.monthRow}>
                                            <td style={styles.monthCell} colSpan={totalColSpan}>{g.label}</td>
                                        </tr>
                                        {g.rows.map((r, idx) => (
                                            <tr key={r.id} style={idx % 2 === 1 ? styles.rowAlt : null}>
                                                <td style={{ ...styles.td, ...styles.tdLeft }}>
                                                    {r.is_reconciled && <span title="Reconciled — locked from auto-sync" style={{ marginRight: '4px' }}>🔒</span>}
                                                    {fmtDate(r._date)}
                                                </td>
                                                {COLUMNS.map((c) => (<td key={c.key} style={styles.td}>{fmtInt(r[c.key])}</td>))}
                                                {RATE_COLUMNS.map((c) => (<td key={c.key} style={styles.td}>{fmtPct(r[c.key])}</td>))}
                                                <td style={{ ...styles.td, ...styles.tdLeft }}>{renderStatus(r.status)}</td>
                                                <td style={{ ...styles.td, ...styles.tdLeft }}>
                                                    <button onClick={() => openEdit(r)} style={styles.rowActionBtn}>Edit</button>
                                                    {r.is_reconciled && (
                                                        <button onClick={() => unreconcile(r)} disabled={saving} style={styles.rowActionLink}>Revert</button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr style={styles.totalRow}>
                                            <td style={{ ...styles.td, ...styles.tdLeft, fontWeight: 'bold' }}>{g.label} Total</td>
                                            {COLUMNS.map((c) => (<td key={c.key} style={{ ...styles.td, fontWeight: 'bold' }}>{fmtInt(g.totals[c.key])}</td>))}
                                            {RATE_COLUMNS.map((c) => (<td key={c.key} style={{ ...styles.td, fontWeight: 'bold' }}>{fmtPct(g.totals[c.key])}</td>))}
                                            <td style={{ ...styles.td, ...styles.tdLeft }} />
                                            <td style={{ ...styles.td, ...styles.tdLeft }} />
                                        </tr>
                                    </React.Fragment>
                                ))}
                                <tr style={styles.grandTotalRow}>
                                    <td style={{ ...styles.td, ...styles.tdLeft, fontWeight: 'bold' }}>Quarter / Total</td>
                                    {COLUMNS.map((c) => (<td key={c.key} style={{ ...styles.td, fontWeight: 'bold' }}>{fmtInt(grandTotal[c.key])}</td>))}
                                    {RATE_COLUMNS.map((c) => (<td key={c.key} style={{ ...styles.td, fontWeight: 'bold' }}>{fmtPct(grandTotal[c.key])}</td>))}
                                    <td style={{ ...styles.td, ...styles.tdLeft }} />
                                    <td style={{ ...styles.td, ...styles.tdLeft }} />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editRow && (
                <div style={styles.modalOverlay} onClick={closeEdit}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Reconcile — {siteFilter}</h3>
                        <p style={styles.modalSub}>
                            {fmtDate(editRow._date)} · Edit the counts below. RFPA / SCR / AVG are
                            recalculated automatically. Saving marks this day as <strong>Reconciled</strong> and
                            locks it from the daily auto-sync.
                        </p>

                        <div style={styles.fieldGrid}>
                            {EDIT_FIELDS.map((f) => (
                                <div key={f.key}>
                                    <label style={styles.fieldLabel}>{f.label}</label>
                                    <input
                                        type="number"
                                        value={form[f.key]}
                                        onChange={(e) => onFormChange(f.key, e.target.value)}
                                        style={styles.fieldInput}
                                    />
                                </div>
                            ))}
                        </div>

                        <div style={styles.previewBox}>
                            <div>
                                <div style={styles.statLabel}>RFPA</div>
                                <div style={{ fontWeight: 'bold' }}>{fmtPct(preview.rfpa)}</div>
                            </div>
                            <div>
                                <div style={styles.statLabel}>SCR</div>
                                <div style={{ fontWeight: 'bold' }}>{fmtPct(preview.scr)}</div>
                            </div>
                            <div>
                                <div style={styles.statLabel}>AVG</div>
                                <div style={{ fontWeight: 'bold' }}>{fmtPct(preview.avg)}</div>
                            </div>
                        </div>

                        <div style={styles.modalActions}>
                            <button onClick={closeEdit} disabled={saving} style={styles.secondaryBtn}>Cancel</button>
                            <button onClick={saveReconcile} disabled={saving} style={styles.primaryBtn}>
                                {saving ? 'Saving...' : 'Save Reconciled'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RfpaScrPage;
